import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Card';
import { Icon } from '@/components/ui/Icon';
import { Dialpad } from '@/components/telephony/Dialpad';
import { useSipStore, invalidNumberReason } from '../stores/sipStore';
import { useLiveCall } from '../stores/liveCall';
import { useSoftphoneWindow } from '../stores/softphoneWindow';
import { useDialInput } from '../stores/dialInput';
import { useAudioDevices } from '../stores/audioDevices';
import { agentApi, type Favorite, type CallRow } from '@/shared/api/agentApi';
import { toast } from '@/components/ui/Toast';
import { ConferencePanel } from './ConferencePanel';
import { playHoldSound } from '../lib/holdSound';

// KLIENTO softphone palette — matches the Chrome dialer extension so the in-system
// softphone looks identical. Local constants (not global tokens) keep the look
// consistent regardless of the surrounding theme.
const K_ACCENT = '#2DACDF';
const K_ACCENT_HOVER = '#2691BB';
const K_ACCENT_50 = '#F0F9FD';
const K_ACCENT_100 = '#E0F3FA';
const K_ACCENT_700 = '#1E7293';
const K_INK = '#16242E';
const K_DANGER = '#D9534F';
const K_OK = '#1F7A50';
const K_LINE = '#E6ECEF';
const K_SLATE = '#5C6B75';
const K_MIST100 = '#EEF3F5';
const K_MONO = "'JetBrains Mono', var(--font-mono, ui-monospace), monospace";

// KLIENTO topbar toggle (checkbox + small label).
const KTOGGLE: React.CSSProperties = {
  display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 5,
  fontSize: 11, fontWeight: 500, color: K_SLATE, cursor: 'pointer', whiteSpace: 'nowrap',
};

// KLIENTO status dot: 9px circle with a soft 3px halo, colored by call state
// (blue ready / green on-call / amber ringing / red error / grey idle).
function statusDot(state: string): React.CSSProperties {
  const base: React.CSSProperties = { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 };
  if (state === 'on-call' || state === 'on-hold') return { ...base, background: K_OK, boxShadow: '0 0 0 3px #E7F6EE' };
  if (state === 'ringing-in' || state === 'ringing-out') return { ...base, background: '#E8A33D', boxShadow: '0 0 0 3px #FBF0DC' };
  if (state === 'error') return { ...base, background: K_DANGER, boxShadow: '0 0 0 3px #F6E2E1' };
  if (state === 'ready') return { ...base, background: K_ACCENT, boxShadow: `0 0 0 3px ${K_ACCENT_100}` };
  return { ...base, background: '#93A1AB' }; // idle / registering
}

// A 46px circular in-call control (mute/hold/conf/hangup/answer), styled like the
// Chrome dialer: neutral mist by default, ink fill when toggled active, semantic
// red/green for hangup/answer.
function roundBtn(variant: 'plain' | 'active' | 'danger' | 'ok' | 'disabled'): React.CSSProperties {
  const base: React.CSSProperties = {
    width: 46, height: 46, borderRadius: '50%', padding: 0,
    border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 150ms ease, filter 150ms ease',
  };
  switch (variant) {
    case 'active': return { ...base, background: K_INK, color: '#fff' };
    case 'danger': return { ...base, background: K_DANGER, color: '#fff' };
    case 'ok': return { ...base, background: K_OK, color: '#fff' };
    case 'disabled': return { ...base, background: '#EEF3F5', color: '#93A1AB', cursor: 'not-allowed' };
    default: return { ...base, background: '#EEF3F5', color: K_INK, cursor: 'pointer' };
  }
}

// Full set of presence states the backend accepts (matches the Settings page).
// 'oncall' is driven automatically by call state, so it's shown as read-only
// status rather than a manually-clickable button.
const PRESENCE: { id: string; label: string; tone: string; manual: boolean }[] = [
  { id: 'available', label: 'זמין', tone: 'ok', manual: true },
  { id: 'oncall', label: 'בשיחה', tone: 'info', manual: false },
  { id: 'acw', label: 'אחרי שיחה', tone: 'warn', manual: true },
  { id: 'break', label: 'הפסקה', tone: 'warn', manual: true },
  { id: 'lunch', label: 'ארוחת צהריים', tone: 'warn', manual: true },
  { id: 'paperwork', label: 'עבודת ניירת', tone: 'warn', manual: true },
  { id: 'training', label: 'הכשרה', tone: 'busy', manual: true },
  { id: 'offline', label: 'מנותק', tone: 'muted', manual: true },
];

export interface SoftphonePanelProps {
  /** Hide the Card chrome (the floating widget supplies its own frame). */
  bare?: boolean;
  /** Hide the dock/float toggle (e.g. when the host already shows one). */
  hideFloatToggle?: boolean;
}

export function SoftphonePanel({ bare, hideFloatToggle }: SoftphonePanelProps = {}) {
  const { t } = useTranslation();
  const floating = useSoftphoneWindow((s) => s.floating);
  const setFloating = useSoftphoneWindow((s) => s.setFloating);
  const state = useSipStore((s) => s.state);
  const registered = useSipStore((s) => s.registered);
  const noLine = useSipStore((s) => s.noLine);
  const lineError = useSipStore((s) => s.error);
  const reconnect = useSipStore((s) => s.reconnect);
  const otherTab = useSipStore((s) => s.otherTab || s.otherDevice !== null);
  const active = useSipStore((s) => s.active);
  const incoming = useSipStore((s) => s.incoming);
  const answer = useSipStore((s) => s.answer);
  const reject = useSipStore((s) => s.reject);
  const dial = useSipStore((s) => s.dial);
  const hangup = useSipStore((s) => s.hangup);
  const muteFn = useSipStore((s) => s.mute);
  const holdFn = useSipStore((s) => s.hold);
  const unholdFn = useSipStore((s) => s.unhold);
  const recordCalls = useSipStore((s) => s.recordCalls);
  const noiseSuppression = useAudioDevices((s) => s.noiseSuppression);
  const setNoiseSuppression = useAudioDevices((s) => s.setNoiseSuppression);

  const creds = useSipStore((s) => s.creds);
  const dids = creds?.dids ?? [];

  // The number field is store-backed (survives remount on call-end/wrap-up),
  // exposed with a useState-compatible signature so all call sites (including
  // functional updaters `setInput(prev => ...)`) work unchanged.
  const input = useDialInput((s) => s.value);
  const setInput = useCallback((next: string | ((prev: string) => string)) => {
    const cur = useDialInput.getState().value;
    useDialInput.getState().set(typeof next === 'function' ? next(cur) : next);
  }, []);
  const [presence, setPresence] = useState('offline');
  const [callerId, setCallerId] = useState<string>('');
  const [callerName, setCallerName] = useState<string | null>(null);
  // Uploaded music-on-hold, played to the agent as a hold confirmation.
  const [holdSoundUrl, setHoldSoundUrl] = useState<string | null>(null);
  // מועדפי חיוג מהיר.
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [favBusy, setFavBusy] = useState(false);
  // Which tab is showing in the idle dialer (chrome-extension style tabbar).
  const [tab, setTab] = useState<'dialer' | 'history' | 'favorites'>('dialer');
  // Conference UI is opt-in per call — opened from the in-call controls row.
  const [confOpen, setConfOpen] = useState(false);
  // Recent-calls list for the history tab (lazy-loaded on first open).
  const [history, setHistory] = useState<CallRow[] | null>(null);

  // טעינת מועדפים פעם אחת.
  useEffect(() => {
    void agentApi.favorites().then(setFavorites).catch(() => {});
  }, []);

  // Lazy-load recent calls the first time the history tab is opened.
  useEffect(() => {
    if (tab !== 'history' || history !== null) return;
    void agentApi.calls()
      .then((r) => setHistory((r?.data ?? []).slice(0, 30)))
      .catch(() => setHistory([]));
  }, [tab, history]);

  useEffect(() => {
    void agentApi
      .settings()
      .then((d) => {
        const s = d as { presence?: { status?: string }; hold_sound_url?: string | null };
        setPresence(s?.presence?.status ?? 'offline');
        setHoldSoundUrl(s?.hold_sound_url ?? null);
      })
      .catch(() => {});
  }, []);

  // 🔴 מי באמת על הקו (16/09/2026): ב-SIP, `remote_identity` של הרגל
  // שהסופטפון מחזיק הוא הצד השני **של רגל הנציג** — ובחיוג מקבילי
  // (predictive/סבב) הרגל הזו מקורה במרכזיה, ולכן הנציג ראה את המספר
  // של עצמו במקום את הלקוח. וגרוע מכך: ה-lookup למטה מחפש לפי המספר
  // הזה, לא מוצא, ולכן גם השם נעלם ("בשיחה" במקום שם הלקוח).
  //
  // ל-LiveCallTracker בשרת יש את התשובה הנכונה: `remote` מחושב
  // מאירועי ה-PBX עצמם וזורם לדפדפן ב-SSE (אותו מקור ש-ActiveCallBanner
  // כבר מציג נכון). מעדיפים אותו, ונופלים ל-SIP רק כשהוא חסר או מפגר —
  // כדי שלעולם לא יוצג ריק.
  const liveRemote = useLiveCall((s) => (s.active?.status === 'answered' || s.active?.status === 'ringing' ? s.active?.remote : null));
  const activeNumber = (active ? (liveRemote || active.number) : undefined);
  useEffect(() => {
    if (!activeNumber) { setCallerName(null); return; }
    let alive = true;
    void agentApi.lookup(activeNumber)
      .then((r) => { if (alive) setCallerName(r?.name ?? null); })
      .catch(() => { if (alive) setCallerName(null); });
    return () => { alive = false; };
  }, [activeNumber]);

  // Default the caller-id picker to the agent's default DID once creds arrive.
  useEffect(() => {
    if (!callerId && creds?.defaultCallerDid) {
      setCallerId(creds.defaultCallerDid);
    }
  }, [creds, callerId]);

  async function changePresence(status: string) {
    const prev = presence;
    setPresence(status);
    try {
      // דרך ה-store: "מנותק" גם מנתק את קו ה-SIP (והחזרה רושמת מחדש).
      await useSipStore.getState().setPresence(status);
    } catch {
      setPresence(prev);
      toast(t('common.error'), 'danger');
    }
  }

  const press = useCallback((key: string) => {
    if (useSipStore.getState().state === 'on-call') {
      useSipStore.getState().sendDtmf(key);
      return;
    }
    setInput((prev) => prev + key);
  }, []);

  const doDial = useCallback(async () => {
    const current = useSipStore.getState();
    // Browser softphone dials via JsSIP (real WebRTC audio). The backend
    // ARI-originate path (agentApi.dial) is reserved for click-to-call.
    // NOTE: the number field is kept AFTER dialing (was cleared) so the agent
    // can redial the same number or edit it — clearing it on every call meant
    // the number "disappeared" the moment a call ended.
    setInput((num) => {
      // שדה ריק = חיוג חוזר: הלחיצה הראשונה מחזירה את המספר האחרון, השנייה
      // מחייגת אליו. השדה מתנקה בסוף שיחה, וכך אין צורך להקליד מחדש.
      if (!num) {
        const last = useDialInput.getState().recallLast();
        if (last) {
          toast(`המספר האחרון: ${last} — לחץ שוב כדי לחייג`, 'info');

          return last;
        }

        return num;
      }
      // מספר פסול נעצר כאן עם הודעה גלויה. בלי זה הוא יצא כ-INVITE ונכשל
      // בשקט אצל הספק — הלחיצה על "חייג" פשוט לא עשתה כלום.
      const invalid = invalidNumberReason(num);
      if (invalid) { toast(invalid, 'danger'); return num; }
      void (async () => {
        try { await current.dial(num, callerId || undefined); } catch { toast(t('common.error'), 'danger'); }
      })();
      return num;
    });
  }, [callerId, t]);

  function clear() {
    setInput((prev) => prev.slice(0, -1));
  }

  // הוספת המספר המחויג כעת למועדפים (עם שם אופציונלי).
  const addCurrentFavorite = useCallback(async () => {
    const number = useDialInput.getState().value.trim();
    if (!number) { toast('הקלד מספר קודם', 'warn'); return; }
    if (favorites.some((f) => f.number === number || f.display === number)) { toast('המספר כבר במועדפים', 'warn'); return; }
    const name = window.prompt('שם למועדף (אופציונלי):', '') ?? undefined;
    setFavBusy(true);
    try {
      const fav = await agentApi.addFavorite(number, name?.trim() || undefined);
      // updateOrCreate בשרת — מחליף רשומה קיימת לפי המספר המנורמל.
      setFavorites((prev) => [fav, ...prev.filter((f) => f.id !== fav.id)]);
      toast('נוסף למועדפים', 'ok');
    } catch {
      toast('שגיאה בהוספה למועדפים', 'danger');
    } finally {
      setFavBusy(false);
    }
  }, [favorites]);

  const dialFavorite = useCallback((fav: Favorite) => {
    const invalid = invalidNumberReason(fav.number);
    if (invalid) { toast(invalid, 'danger'); return; }
    void (async () => {
      try { await useSipStore.getState().dial(fav.number, callerId || undefined); }
      catch { toast(t('common.error'), 'danger'); }
    })();
  }, [callerId, t]);

  const removeFavorite = useCallback(async (fav: Favorite) => {
    setFavorites((prev) => prev.filter((f) => f.id !== fav.id)); // אופטימי
    try {
      await agentApi.removeFavorite(fav.id);
    } catch {
      setFavorites((prev) => [fav, ...prev]); // שחזור בכשל
      toast('שגיאה בהסרה', 'danger');
    }
  }, []);

  // Paste a number: strip everything that isn't a dialable char. Handles the
  // shapes people copy — "050-123-4567", "(03) 123 4567", "+972 50 123 4567".
  // Keeps a leading + and any * / # (extensions/IVR). Appends to the current
  // number so a paste can extend a partially-typed one.
  const pasteNumber = useCallback((raw: string) => {
    if (!raw) return;
    const cleaned = normalizeDialInput(raw);
    if (!cleaned) return;
    setInput((prev) => prev + cleaned);
  }, []);

  const pasteFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      pasteNumber(text);
    } catch {
      toast('לא ניתן לגשת ללוח. הדבק עם Ctrl+V', 'warn');
    }
  }, [pasteNumber]);

  const copyToClipboard = useCallback(async (value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast('המספר הועתק', 'ok');
    } catch {
      toast(t('common.error'), 'danger');
    }
  }, [t]);

  const isOnCall = state === 'on-call' || state === 'on-hold' || state === 'ringing-out';
  // Conference/add-person needs BOTH a resolved backend call id (WebRTC calls get
  // it a beat after connect) AND a truly connected call — not while still ringing.
  const canConference = active?.callId != null && (state === 'on-call' || state === 'on-hold');
  // A new call always starts with the conference panel closed.
  useEffect(() => { if (!canConference) setConfOpen(false); }, [canConference]);
  const isRingingIn = state === 'ringing-in';
  // NOTE: wrap-up is intentionally NOT excluded below — the wrap-up summary is a
  // separate portal overlay, so keeping the dial field visible during wrap-up
  // means the number the agent typed stays on screen (it's retained in the
  // dialInput store) instead of appearing to vanish when a call ends.
  // The outbound dialpad/number-input/dial-button views must never show while a
  // call is coming in — otherwise the panel "looks like it's dialing out".
  // wrap-up is included so the retained number stays visible after a call (the
  // blocking wrap-up modal, when enabled, is a scrim on top that still forces
  // documentation before the agent can actually use the dialer).
  const showDialUi = !isOnCall && !isRingingIn;

  // Physical-keyboard dialing: type digits / * / # to build the number,
  // Backspace to delete, Enter to dial, Ctrl/Cmd+C to copy the number.
  // Disabled while typing in a real input field elsewhere so it never hijacks
  // other forms.
  useEffect(() => {
    if (!showDialUi) return;
    const inField = (el: HTMLElement | null) => {
      const tag = el?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el?.isContentEditable;
    };
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (inField(el)) return;
      // Ctrl/Cmd+C copies the built number (when nothing else is selected).
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
        const sel = window.getSelection?.()?.toString();
        if (!sel && input) {
          e.preventDefault();
          void copyToClipboard(input);
        }
        return;
      }
      // Let Ctrl/Cmd+V fall through to the paste handler below.
      if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) return;
      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        setInput((prev) => prev + e.key);
      } else if (e.key === '+') {
        e.preventDefault();
        setInput((prev) => prev + '+');
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setInput((prev) => prev.slice(0, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        void doDial();
      }
    };
    // A real 'paste' event gives reliable clipboard access across browsers
    // (reading the clipboard from a keydown handler is blocked in many).
    const onPaste = (e: ClipboardEvent) => {
      if (inField(e.target as HTMLElement | null)) return;
      const text = e.clipboardData?.getData('text') ?? '';
      if (!text) return;
      e.preventDefault();
      pasteNumber(text);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('paste', onPaste);
    };
  }, [showDialUi, state, doDial, input, copyToClipboard, pasteNumber]);

  const inner = (
      <div className="stack" style={{ gap: 10 }}>
        {/* Topbar — status pill on the right, quick toggles + presence + exit on
            the left. Mirrors the Chrome dialer extension's header. */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          paddingBottom: 8, borderBottom: `1px solid ${K_LINE}`,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: K_INK }}>
            <span aria-hidden style={statusDot(state)} />
            {noLine ? 'אין שלוחה' : stateLabel(state, t)}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* presence as a compact select (was a row of chips) */}
            <select
              aria-label="זמינות"
              value={presence}
              onChange={(e) => changePresence(e.target.value)}
              style={{
                fontSize: 11.5, fontWeight: 500, color: K_SLATE, background: 'transparent',
                border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '2px 4px',
              }}
            >
              {PRESENCE.filter((p) => p.manual || presence === p.id).map((p) => (
                <option key={p.id} value={p.id} disabled={!p.manual}>{p.label}</option>
              ))}
            </select>
            {/* noise-suppression toggle */}
            <label style={KTOGGLE} title="סינון רעשי רקע על המיקרופון">
              <input type="checkbox" aria-label="סינון רעשים" checked={noiseSuppression} onChange={() => setNoiseSuppression(!noiseSuppression)} style={{ accentColor: K_ACCENT }} />
              סינון רעשים
            </label>
            {!hideFloatToggle && (
              <button
                type="button"
                onClick={() => setFloating(!floating)}
                aria-label={floating ? 'עגן למסך' : 'נתק לחלון צף'}
                title={floating ? 'עגן למסך' : 'נתק לחלון צף'}
                style={{ height: 26, width: 26, padding: 0, border: 'none', background: 'transparent', color: K_SLATE, cursor: 'pointer', borderRadius: 6 }}
              >
                <Icon name={floating ? 'target' : 'expand'} size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Tabbar — חייגן / היסטוריה / מועדפים, like the extension. Hidden during
            an active/incoming call so the call UI takes over. */}
        {showDialUi && !isRingingIn && (
          <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${K_LINE}` }}>
            {([['dialer', 'חייגן'], ['history', 'היסטוריה'], ['favorites', 'מועדפים']] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                style={{
                  flex: 1, background: 'none', border: 'none', padding: '8px 4px', fontFamily: 'inherit',
                  fontSize: 13, cursor: 'pointer',
                  color: tab === id ? K_ACCENT_HOVER : K_SLATE,
                  fontWeight: tab === id ? 600 : 400,
                  borderBottom: `2px solid ${tab === id ? K_ACCENT_HOVER : 'transparent'}`,
                  marginBottom: -1,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Conference control — opens on demand from the in-call controls row:
            add unlimited agents or external numbers, with a live participants list. */}
        {canConference && confOpen && active?.callId != null && (
          <ConferencePanel callId={active.callId} />
        )}

        {/* Active call summary */}
        {active && (
          <div
            style={{
              padding: '14px 12px',
              background: K_ACCENT_50,
              border: `1px solid ${K_ACCENT_100}`,
              borderRadius: 14,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: K_ACCENT_700 }}>
              {callerName ? `★ ${callerName}` : stateLabel(state, t)}
            </div>
            <div dir="ltr" style={{ fontSize: 19, fontWeight: 600, fontFamily: K_MONO, color: K_INK, marginTop: 2 }}>
              {activeNumber ?? active.number}
            </div>
            <div style={{ fontSize: 13, color: K_ACCENT_HOVER, fontFamily: K_MONO, marginTop: 2 }}>
              {fmtDur(active.durationSec)}
              {active.muted && ' • מושתק'}
              {active.onHold && ' • מוחזק'}
            </div>
          </div>
        )}

        {/* שיחה נכנסת — מוצגת במסך אחד בלבד (IncomingCallModal), עם פרטי
            הלקוח, הקמפיין וההיסטוריה. עד 07/09/2026 הופיע כאן בלוק שני עם
            אותם כפתורים ופחות מידע, ושני המסכים נראו במקביל. כאן נשארת
            רק אינדיקציה שהשיחה מצלצלת. */}
        {isRingingIn && incoming && (
          <div
            role="status"
            className="stack"
            style={{ gap: 4, padding: '10px 12px', background: 'var(--warn-soft)', borderRadius: 'var(--r-2)', textAlign: 'center' }}
          >
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 'var(--t-sm)', fontWeight: 600 }}>
              <Icon name="phoneIn" size={13} /> שיחה נכנסת
            </div>
            <div dir="ltr" style={{ fontSize: 'var(--t-sm)', fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>
              {incoming.displayName || incoming.from}
            </div>
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>ענה או דחה בחלון השיחה</div>
          </div>
        )}

        {/* מצב הקו כשאי אפשר לחייג. עד עכשיו "חייג" על קו לא רשום כתב שגיאה
            לקונסול והצביע באדום — והנציג לא ידע אם חסרה לו שלוחה, אם החיבור
            נפל, או אם צריך לחכות. */}
        {showDialUi && !registered && (
          <div
            role="status"
            style={{
              padding: '8px 10px', borderRadius: 10, fontSize: 12, lineHeight: 1.45,
              background: noLine ? '#F7FAFB' : state === 'error' ? '#FDF2F1' : '#FFF8E8',
              border: `1px solid ${noLine ? '#E6ECEF' : state === 'error' ? '#F3C9C6' : '#F5E1B3'}`,
              color: K_INK, display: 'flex', flexDirection: 'column', gap: 6,
            }}
          >
            <span>
              {noLine
                ? 'לא הוקצתה שלוחה למשתמש זה — חיוג מהדפדפן יתאפשר אחרי שמנהל יקצה שלוחה בהגדרות הנציגים.'
                : state === 'registering'
                  ? 'מתחבר לקו…'
                  : lineError ?? 'הקו לא מחובר.'}
            </span>
            {!noLine && state !== 'registering' && (
              <button
                type="button"
                className="btn ghost"
                style={{ alignSelf: 'flex-start', fontSize: 12, padding: '4px 10px' }}
                onClick={() => { void reconnect().then((ok) => { if (!ok) toast('החיבור לקו נכשל', 'danger'); }); }}
              >
                {otherTab ? 'העבר לכאן' : 'התחבר מחדש'}
              </button>
            )}
          </div>
        )}

        {/* Caller-ID picker — only when the agent has more than one DID */}
        {showDialUi && tab === 'dialer' && dids.length > 1 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            מזהה מתקשר
            <select
              dir="ltr"
              value={callerId}
              onChange={(e) => setCallerId(e.target.value)}
              className="input"
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              {dids.map((d) => (
                <option key={d.number} value={d.number}>
                  {d.label ? `${d.label} (${d.number})` : d.number}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Number input (only when idle, dialer tab) */}
        {showDialUi && tab === 'dialer' && (
          <div
            dir="ltr"
            style={{
              padding: '10px 12px',
              background: '#F7FAFB',
              border: '1px solid #E6ECEF',
              borderRadius: 12,
              fontSize: 19,
              fontFamily: K_MONO,
              letterSpacing: '0.02em',
              color: K_INK,
              minHeight: 46,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={input ? undefined : { color: '#93A1AB', fontSize: 12.5, fontFamily: 'var(--font-sans, sans-serif)', letterSpacing: 'normal' }}>
              {input || 'הקלד או הדבק מספר וחייג ב-Enter'}
            </span>
          </div>
        )}

        {/* פעולות על המספר — שורה נפרדת מתחת לשדה ולא בתוכו, כדי שהמספר
            עצמו יישאר קריא ולא יידחס מול חמישה אייקונים.
            אותו תנאי בדיוק כמו השדה (שורה 499): בטאב היסטוריה/מועדפים אין
            שדה מספר, ושורת פעולות מרחפת בלעדיו היא חסרת משמעות. */}
        {showDialUi && tab === 'dialer' && (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexShrink: 0 }}>
              {/* Paste a number from the clipboard (Ctrl+V also works). */}
              <button type="button" className="btn ghost" onClick={pasteFromClipboard} aria-label="הדבק מספר" title="הדבק מספר (Ctrl+V)">
                <Icon name="clipboard" size={16} />
              </button>
              {/* Add the typed number to favorites. */}
              {input && (
                <button type="button" className="btn ghost" onClick={addCurrentFavorite} disabled={favBusy} aria-label="הוסף למועדפים" title="הוסף למועדפים">
                  <Icon name="star" size={16} />
                </button>
              )}
              {/* Copy the built number (Ctrl+C also works). */}
              {input && (
                <button type="button" className="btn ghost" onClick={() => copyToClipboard(input)} aria-label="העתק מספר" title="העתק מספר (Ctrl+C)">
                  <Icon name="copy" size={16} />
                </button>
              )}
              {input && (
                <button type="button" className="btn ghost" onClick={clear} aria-label="מחק ספרה" title="מחק ספרה">
                  ←
                </button>
              )}
              {input && (
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setInput('')}
                  aria-label="נקה מספר"
                  title="נקה את כל המספר"
                >
                  ✕
                </button>
              )}
          </span>
        )}

        {/* Dialpad / DTMF — dialer tab when idle (to dial), or during a call (DTMF). */}
        {((showDialUi && tab === 'dialer') || isOnCall) && (
          <Dialpad onPress={press} />
        )}

        {/* מועדפי חיוג מהיר — טאב "מועדפים": הקלקה מחייגת, ✕ מסירה. */}
        {showDialUi && tab === 'favorites' && (
          <div className="stack" style={{ gap: 6 }}>
            {favorites.length === 0 && (
              <div style={{ fontSize: 12.5, color: '#93A1AB', textAlign: 'center', padding: '20px 0' }}>
                אין עדיין מועדפים. הוסף מספר מטאב החייגן.
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {favorites.map((fav) => (
                <div
                  key={fav.id}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    border: '1px solid var(--line)', borderRadius: 'var(--r-pill)',
                    background: 'var(--surface)', paddingInlineStart: 10, overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => dialFavorite(fav)}
                    title="חייג"
                    style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: '4px 0' }}
                  >
                    {fav.name && <span style={{ fontSize: 'var(--t-xs)', fontWeight: 600 }}>{fav.name}</span>}
                    <span dir="ltr" style={{ fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)', color: fav.name ? 'var(--muted)' : 'var(--text)' }}>
                      {fav.display || fav.number}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFavorite(fav)}
                    aria-label="הסר ממועדפים"
                    title="הסר"
                    className="btn ghost"
                    style={{ height: 24, width: 24, padding: 0, fontSize: 12 }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* טאב "היסטוריה" — שיחות אחרונות של הנציג; הקלקה מחייגת חזרה. */}
        {showDialUi && tab === 'history' && (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {history === null ? (
              <div style={{ fontSize: 12.5, color: '#93A1AB', textAlign: 'center', padding: '20px 0' }}>טוען…</div>
            ) : history.length === 0 ? (
              <div style={{ fontSize: 12.5, color: '#93A1AB', textAlign: 'center', padding: '20px 0' }}>אין שיחות אחרונות.</div>
            ) : (
              history.map((c) => {
                // CallRow exposes from/to (the other party depends on direction).
                const num = c.direction === 'inbound' ? c.from : c.to;
                const name = c.other_party?.name || null;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setInput(num || ''); setTab('dialer'); }}
                    style={{
                      width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                      padding: '9px 6px', border: 'none', borderBottom: `1px solid ${K_MIST100}`,
                      background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'start',
                    }}
                  >
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                      {name && <span style={{ fontSize: 12.5, fontWeight: 600, color: K_INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{name}</span>}
                      <span dir="ltr" style={{ fontSize: 13, fontFamily: K_MONO, color: name ? K_SLATE : K_INK }}>{num || '—'}</span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: K_SLATE, flexShrink: 0 }}>
                      {c.direction === 'inbound' ? '↙ נכנסת' : c.direction === 'internal' ? '↔ פנימית' : '↗ יוצאת'}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        )}

        {/* Live recording indicator during a call (recording is always on by
            policy; the toggle for noise-suppression now lives in the topbar). */}
        {isOnCall && recordCalls && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, color: K_DANGER }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: K_DANGER }} /> מקליט
          </div>
        )}

        {/* Wrap-up is handled by the global blocking WrapupOverlay (AgentLayout). */}

        {/* Action row (incoming call uses its own answer/reject buttons above) */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          {isRingingIn ? null : showDialUi ? (
            <button
              type="button"
              onClick={doDial}
              className="btn"
              style={{
                width: '100%', height: 48, borderRadius: 12,
                background: input ? K_ACCENT : '#EEF3F5',
                color: input ? '#fff' : '#93A1AB',
                fontSize: 15.5, fontWeight: 600, gap: 7,
                border: 'none',
                cursor: input ? 'pointer' : 'not-allowed',
                transition: 'background 150ms ease',
              }}
              disabled={!input || noLine}
              title={noLine ? 'לא הוקצתה שלוחה למשתמש זה' : undefined}
              onMouseEnter={(e) => { if (input) e.currentTarget.style.background = K_ACCENT_HOVER; }}
              onMouseLeave={(e) => { if (input) e.currentTarget.style.background = K_ACCENT; }}
            >
              <Icon name="phone" size={18} />
              חייג
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => muteFn()}
                className="btn"
                style={roundBtn(active?.muted ? 'active' : 'plain')}
                aria-label="mute"
              >
                <Icon name="mic" size={19} />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (active?.onHold) {
                    unholdFn();
                  } else {
                    holdFn();
                    // Confirm the hold audibly (uploaded MOH clip, else a blip).
                    playHoldSound(holdSoundUrl);
                  }
                }}
                className="btn"
                style={roundBtn(active?.onHold ? 'active' : 'plain')}
                aria-label="hold"
              >
                <Icon name="pause" size={19} />
              </button>
              {canConference && (
                <button
                  type="button"
                  onClick={() => setConfOpen((v) => !v)}
                  className="btn"
                  style={roundBtn(confOpen ? 'active' : 'plain')}
                  aria-label="conference"
                  title="צירוף לוועידה"
                >
                  <Icon name="users" size={19} />
                </button>
              )}
              <button
                type="button"
                onClick={hangup}
                className="btn"
                style={roundBtn('danger')}
                aria-label="hangup"
              >
                <Icon name="hang" size={20} />
              </button>
            </>
          )}
        </div>
      </div>
  );

  if (bare) return inner;
  return <Card padded>{inner}</Card>;
}


function stateLabel(state: string, t: (k: string) => string): string {
  const map: Record<string, string> = {
    idle: 'מנותק',
    registering: 'מתחבר',
    ready: 'זמין',
    'ringing-in': 'מצלצל',
    'ringing-out': 'מחייג',
    'on-call': 'בשיחה',
    'on-hold': 'מוחזק',
    'wrap-up': 'סיכום',
    error: t('common.error'),
  };
  return map[state] ?? state;
}

function fmtDur(s: number): string {
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}

/**
 * Reduce arbitrary pasted text to dialable characters: digits, * and # (IVR/
 * extensions), and a single leading '+' (international prefix). Everything else
 * — spaces, dashes, parentheses, dots, "tel:" prefixes, letters — is dropped.
 */
export function normalizeDialInput(raw: string): string {
  const text = String(raw).trim().replace(/^tel:/i, '');
  const hadPlus = text.trimStart().startsWith('+');
  const digits = text.replace(/[^0-9*#]/g, '');
  return (hadPlus ? '+' : '') + digits;
}
